import { ExternalLink } from 'lucide-react'
import { ReactNode } from 'react'

import { DefinitionList, DetailSection } from './detail-section'
import { LoginAsEpilotUser } from './login-as-epilot-user'
import { formatDate } from './organization-header'
import { OrganizationTab } from './organization-tabs'
import { formatQuota, getPricingTierRows } from './pricing-tier-rows'

import { PricingTierComponents } from '@/api'
import { DeltaText, KpiTile } from '@/components/activity/activity-panel'
import {
  getFieldValue,
  getHubspotCompanyUrl
} from '@/components/organization-hubspot-data'
import { Button } from '@/components/ui/button'
import { Icons } from '@/components/ui/icons'
import { IOrganization } from '@/models'
import { useQueryGetOrgActivity } from '@/services'
import { useQueryGetOrganizationHubspotData } from '@/services/hubspot.service'
import { formatNumber, formatPercent, getSeats } from '@/utils/activity'

/** same params as the Activity tab's default view, so both share one request */
const ACTIVITY_PARAMS = { granularity: 'week', top_limit: 10 } as const

export type OrganizationUsage = {
  actualUsers: number
  maxUsersLastMonth: number
  maxBillableUsersLastMonth: number
  additionalPaidUsers: number
  freeUserLimit: number
  actualCustomers: number
  maxCustomers: number
  activePartnerships: number
  actualPartnerUsers: number
  maxPartnerUsers: number
  endCustomerPortalUsers: number
  installerPortalUsers: number
}

type OverviewTabProps = {
  orgId: string
  organization?: IOrganization
  parentOrganizationName?: string | null
  elasticCluster?: string
  usage: OrganizationUsage
  isUsageLoading: boolean
  pricingTierName?: string
  pricingTier?: PricingTierComponents.Schemas.PricingTier
  isPricingTierLoading?: boolean
  /** an org marked for cleanup can't be logged into */
  isMarkedForCleanup?: boolean
  onOpenTab: (tab: OrganizationTab) => void
}

const OpenTabButton = ({
  children,
  onClick
}: {
  children: ReactNode
  onClick: () => void
}) => (
  <Button className="h-auto p-0" onClick={onClick} variant="link">
    {children}
  </Button>
)

export const OverviewTab = ({
  orgId,
  organization,
  parentOrganizationName,
  elasticCluster,
  usage,
  isUsageLoading,
  pricingTierName,
  pricingTier,
  isPricingTierLoading,
  isMarkedForCleanup,
  onOpenTab
}: OverviewTabProps) => {
  const activity = useQueryGetOrgActivity(orgId, ACTIVITY_PARAMS)
  const summary = activity.data?.summary
  const seats = getSeats(summary)
  const needsAttention = summary
    ? summary.dormant_30d + summary.never_seen
    : undefined

  const hubspot = useQueryGetOrganizationHubspotData(orgId)

  const tierRows = getPricingTierRows(pricingTier)
  const overriddenRows = tierRows.filter((row) => row.overridden)
  const tierRow = (key: string) => tierRows.find((row) => row.key === key)

  return (
    <div className="space-y-6">
      <section
        aria-label="Quick actions"
        className="flex flex-wrap items-center gap-x-6 gap-y-3 rounded-lg border bg-card px-5 py-3"
      >
        <h2 className="text-sm font-semibold">Quick actions</h2>
        <LoginAsEpilotUser
          disabled={isMarkedForCleanup}
          orgId={orgId}
          orgName={organization?.name}
        />
      </section>

      <section aria-label="Usage" className="space-y-3">
        <div className="flex items-baseline justify-between gap-3">
          <h2 className="text-base font-semibold">Usage</h2>
          <span className="text-xs text-muted-foreground">
            Data points refresh daily
          </span>
        </div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-5">
          <KpiTile
            hint={`Max ${formatNumber(usage.maxUsersLastMonth)} last month`}
            isLoading={isUsageLoading}
            label="Users"
            value={formatNumber(usage.actualUsers)}
          />
          <KpiTile
            hint={
              usage.additionalPaidUsers > 0
                ? `${formatNumber(
                    usage.additionalPaidUsers
                  )} above the free limit of ${formatNumber(
                    usage.freeUserLimit
                  )}`
                : usage.freeUserLimit > 0
                ? `Free limit ${formatNumber(usage.freeUserLimit)}`
                : 'No free user limit'
            }
            isLoading={isUsageLoading}
            label="Billable users · last month"
            tone={usage.additionalPaidUsers > 0 ? 'warning' : 'default'}
            value={formatNumber(usage.maxBillableUsersLastMonth)}
          />
          <KpiTile
            hint={`Max ${formatNumber(usage.maxCustomers)} last month`}
            isLoading={isUsageLoading}
            label="Customer data points"
            value={formatNumber(usage.actualCustomers)}
          />
          <KpiTile
            hint={`${formatNumber(
              usage.activePartnerships
            )} partnerships · max ${formatNumber(
              usage.maxPartnerUsers
            )} last month`}
            isLoading={isUsageLoading}
            label="Partner users"
            value={formatNumber(usage.actualPartnerUsers)}
          />
          <KpiTile
            hint={`End customer ${formatNumber(
              usage.endCustomerPortalUsers
            )} · Installer ${formatNumber(usage.installerPortalUsers)}`}
            isLoading={isUsageLoading}
            label="Portal users"
            value={formatNumber(
              usage.endCustomerPortalUsers + usage.installerPortalUsers
            )}
          />
        </div>
      </section>

      <div className="grid items-start gap-6 xl:grid-cols-2">
        <div className="space-y-6">
          <DetailSection
            actions={
              <OpenTabButton onClick={() => onOpenTab('settings')}>
                Edit in settings
              </OpenTabButton>
            }
            bodyClassName="px-5 pb-3 pt-1"
            title="Account"
          >
            <DefinitionList
              items={[
                { label: 'Environment', value: organization?.organization_use },
                {
                  label: 'Parent production organization',
                  value: organization?.parent_production_org_id
                    ? parentOrganizationName ??
                      organization.parent_production_org_id
                    : undefined
                },
                {
                  label: 'Subscription',
                  value: organization?.subscription_status
                },
                {
                  label: 'Severity group',
                  value: organization?.severity_group
                },
                {
                  label: 'Customer number',
                  value: organization?.customer_number
                },
                { label: 'Email', value: organization?.email },
                { label: 'Tags', value: organization?.tags?.join(', ') }
              ]}
            />
          </DetailSection>

          <DetailSection
            actions={
              <span className="rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">
                Read-only
              </span>
            }
            bodyClassName="px-5 pb-3 pt-1"
            title="System"
          >
            <DefinitionList
              items={[
                { label: 'ID', value: organization?.id, mono: true },
                { label: 'Symbol', value: organization?.symbol },
                { label: 'Type', value: organization?.type },
                { label: 'Status', value: organization?.status },
                {
                  label: 'Created',
                  value: formatDate(organization?.created_date)
                },
                {
                  label: 'Elastic Search cluster',
                  value: elasticCluster,
                  mono: true
                }
              ]}
            />
          </DetailSection>
        </div>

        <div className="space-y-6">
          <DetailSection
            actions={
              <OpenTabButton onClick={() => onOpenTab('activity')}>
                Open activity
              </OpenTabButton>
            }
            title="360 activity"
          >
            {activity.isError ? (
              <p className="text-sm text-muted-foreground">
                Activity could not be loaded for this organization.
              </p>
            ) : (
              <div className="grid grid-cols-2 gap-3 2xl:grid-cols-4">
                <KpiTile
                  hint="last 15 min"
                  indicator="live"
                  isLoading={activity.isLoading}
                  label="Active now"
                  value={formatNumber(summary?.active_now)}
                />
                <KpiTile
                  hint={
                    summary && (
                      <DeltaText
                        current={summary.active_7d}
                        previous={summary.active_prev_7d}
                        suffix="vs previous 7 days"
                      />
                    )
                  }
                  isLoading={activity.isLoading}
                  label="Active 7 days"
                  value={formatNumber(summary?.active_7d)}
                />
                <KpiTile
                  hint={
                    summary &&
                    (seats !== null
                      ? `${formatNumber(summary.active_30d)} / ${formatNumber(
                          seats
                        )} seats`
                      : `${formatNumber(summary.active_30d)} / ${formatNumber(
                          summary.members
                        )} members`)
                  }
                  isLoading={activity.isLoading}
                  label="Activation · 30d"
                  value={formatPercent(
                    seats !== null
                      ? summary?.activation_rate_30d
                      : summary?.member_activation_rate_30d
                  )}
                />
                <KpiTile
                  hint={
                    summary &&
                    `${formatNumber(
                      summary.dormant_30d
                    )} dormant · ${formatNumber(summary.never_seen)} never seen`
                  }
                  isLoading={activity.isLoading}
                  label="Needs attention"
                  tone={needsAttention ? 'warning' : 'default'}
                  value={formatNumber(needsAttention)}
                />
              </div>
            )}
          </DetailSection>

          <DetailSection
            actions={
              <OpenTabButton onClick={() => onOpenTab('pricing-tier')}>
                Manage
              </OpenTabButton>
            }
            bodyClassName="px-5 pb-3 pt-3"
            title="Pricing tier"
          >
            {isPricingTierLoading ? (
              <Icons.spinner className="h-5 w-5" />
            ) : (
              <>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-base font-semibold">
                    {pricingTierName ?? 'No pricing tier assigned'}
                  </span>
                  {overriddenRows.length > 0 && (
                    <span className="rounded-full bg-amber-500/15 px-2 py-0.5 text-xs font-semibold text-amber-700 dark:text-amber-300">
                      {overriddenRows.length === 1
                        ? '1 override'
                        : `${overriddenRows.length} overrides`}
                    </span>
                  )}
                </div>
                <DefinitionList
                  items={[
                    {
                      label: 'Free users',
                      value: formatQuota(tierRow('free_user')?.value)
                    },
                    {
                      label: 'Active journey limit',
                      value: formatQuota(tierRow('active_journeys')?.value)
                    },
                    {
                      label: 'Overridden settings',
                      value: overriddenRows.length
                        ? overriddenRows.map((row) => row.label).join(', ')
                        : 'None, the tier applies as is'
                    }
                  ]}
                />
              </>
            )}
          </DetailSection>

          <DetailSection
            actions={
              hubspot.data && (
                <OpenTabButton onClick={() => onOpenTab('hubspot-data')}>
                  View data
                </OpenTabButton>
              )
            }
            title="HubSpot company"
          >
            {hubspot.isLoading ? (
              <Icons.spinner className="h-5 w-5" />
            ) : hubspot.data ? (
              <div className="space-y-3">
                <div>
                  <p className="text-sm font-semibold">
                    {hubspot.data.company_name || 'Unknown Company'}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {[
                      hubspot.data.domain,
                      hubspot.data.company_id &&
                        `HubSpot ID ${hubspot.data.company_id}`
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  </p>
                </div>
                <DefinitionList
                  items={[
                    {
                      label: 'Type',
                      value: getFieldValue(hubspot.data, {
                        key: 'type',
                        label: 'Type'
                      })
                    },
                    {
                      label: 'Marktrolle',
                      value: getFieldValue(hubspot.data, {
                        key: 'marktrolle',
                        label: 'Marktrolle'
                      })
                    },
                    {
                      label: 'Company size',
                      value: getFieldValue(hubspot.data, {
                        key: 'company_size',
                        label: 'Company size'
                      })
                    },
                    {
                      label: 'Current MRR',
                      value: getFieldValue(hubspot.data, {
                        key: 'current_mrr',
                        label: 'Current MRR'
                      })
                    }
                  ]}
                />
                {hubspot.data.company_id && (
                  <a
                    className="inline-flex items-center gap-1 text-sm underline-offset-4 hover:underline"
                    href={getHubspotCompanyUrl(hubspot.data.company_id)}
                    rel="noopener noreferrer"
                    target="_blank"
                  >
                    Open in HubSpot
                    <ExternalLink className="h-3 w-3" />
                  </a>
                )}
              </div>
            ) : (
              <div className="flex flex-wrap items-center justify-between gap-3">
                <p className="text-sm text-muted-foreground">
                  No HubSpot company is linked.
                </p>
                <Button
                  onClick={() => onOpenTab('settings')}
                  size="sm"
                  variant="outline"
                >
                  Link in settings
                </Button>
              </div>
            )}
          </DetailSection>
        </div>
      </div>
    </div>
  )
}
